import { type JSX } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from "@fluentui/react-components";
import { ArrowDownloadRegular } from "@fluentui/react-icons/svg/arrow-download";
import { ChatRegular } from "@fluentui/react-icons/svg/chat";
import { InfoRegular } from "@fluentui/react-icons/svg/info";
import { LinkRegular } from "@fluentui/react-icons/svg/link";
import { LockClosedRegular } from "@fluentui/react-icons/svg/lock-closed";
import { useConfig } from "@contexts/ConfigContext";

interface AppHelpMenuProps {
  onSendFeedback: () => void;
}

/**
 * The resources themselves, without a menu around them, so the phone header
 * can fold them into its single overflow menu rather than spending a button
 * of its own on them.
 */
export function AppHelpMenuItems({
  onSendFeedback,
}: AppHelpMenuProps): JSX.Element {
  const { t } = useTranslation();
  const { config } = useConfig();
  const logoBase = config?.regionBaseUrl ?? "";

  const open = (path: string) => () =>
    window.open(path, "_blank", "noreferrer");

  return (
    <>
      <MenuItem icon={<InfoRegular />} onClick={open(`${logoBase}/about`)}>
        {t("layout.sidebar.about")}
      </MenuItem>
      <MenuItem
        icon={<LinkRegular />}
        onClick={open("https://www.linkedin.com/company/onetasklist")}
      >
        {t("layout.sidebar.linkedin")}
      </MenuItem>
      <MenuItem icon={<ChatRegular />} onClick={onSendFeedback}>
        {t("board.feedback.title")}
      </MenuItem>
      {/* The Region builds this for whichever deployment served the page, so the
          package always points back at the environment it was downloaded from. */}
      <MenuItem
        icon={<ArrowDownloadRegular />}
        onClick={open(`${logoBase}/api/teams/package`)}
      >
        {t("layout.sidebar.teamsApp")}
      </MenuItem>
      <MenuItem
        icon={<LockClosedRegular />}
        onClick={open(`${logoBase}/privacy`)}
      >
        {t("layout.sidebar.privacyPolicy")}
      </MenuItem>
      <MenuItem icon={<LinkRegular />} onClick={open(`${logoBase}/terms`)}>
        {t("layout.sidebar.termsAndConditions")}
      </MenuItem>
    </>
  );
}

export function AppHelpMenu({ onSendFeedback }: AppHelpMenuProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          className="app-header__help-btn"
          icon={<InfoRegular />}
          title={t("layout.sidebar.resources")}
          aria-label={t("layout.sidebar.resources")}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <AppHelpMenuItems onSendFeedback={onSendFeedback} />
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
